import React, { useEffect, useRef, useState, forwardRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import CustomCardImportantServices from "./customCardImportantServices";
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import CustomTitle from "../sharedComponents/customTitle";
import { fetchAllStores } from "../../redux/reducer/storesReducer";
import { useLanguageNavigate } from "../../hooks/useLanguageNavigation";
import useLanguageDirection from "../../hooks/useLanguageDirection";

// Custom Arrow Components
const CustomPrevArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-0 z-10 hidden p-2 transition-colors -translate-y-1/2 bg-white rounded-full shadow top-1/2 md:block hover:bg-gray-50"
    style={{ left: "-10px" }}
  >
    <ChevronDoubleLeftIcon className="w-6 h-6 text-black" />
  </button>
);

const CustomNextArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-0 z-10 hidden p-2 transition-colors -translate-y-1/2 bg-white rounded-full shadow top-1/2 md:block hover:bg-gray-50"
    style={{ right: "-10px" }}
  >
    <ChevronDoubleRightIcon className="w-6 h-6 text-black" />
  </button>
);

const ImportantServicesSlider = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { allStores, loading, error } = useSelector((state) => state.stores);
  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);
  const direction = useLanguageDirection();
  const [slidesToShow, setSlidesToShow] = useState(3);
  const navigateWithLang = useLanguageNavigate();

  useEffect(() => {
    dispatch(fetchAllStores());
  }, [dispatch]);


  // Update slides to show based on screen size
  useEffect(() => {
    const updateSlidesToShow = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setSlidesToShow(1.2); // Show 1.2 slides on mobile for partial view
      } else if (width < 1024) {
        setSlidesToShow(2); // Show 2 slides on tablet
      } else {
        setSlidesToShow(3); // Show 3 slides on desktop
      }
    };

    updateSlidesToShow();
    window.addEventListener("resize", updateSlidesToShow);
    return () => window.removeEventListener("resize", updateSlidesToShow);
  }, []);

  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w\-]+/g, "")
      .replace(/\-\-+/g, "-");
  };

  const handleStoreClick = (id, store) => {
    navigateWithLang(`/stores/${encodeURIComponent(id)}`, { state: { store } });
    window.location.reload();
  };

  // Custom autoplay function
  const startCustomAutoplay = () => {
    autoPlayRef.current = setInterval(() => {
      if (sliderRef.current) {
        sliderRef.current.slickNext();
      }
    }, 5000);
  };

  const resetCustomAutoplay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    startCustomAutoplay();
  };

  useEffect(() => {
    // startCustomAutoplay();
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, []);

  // Slick settings
  const sliderSettings = {
    dots: false,
    // infinite: true,
    // speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: false, // We'll handle autoplay manually
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    rtl: direction === "rtl",
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1.2,
          slidesToScroll: 1,
          arrows: false, // Hide arrows on mobile
        },
      },
    ],
    // beforeChange: () => {
    //   resetCustomAutoplay();
    // },
    // onSwipe: () => {
    //   resetCustomAutoplay();
    // },
  };

  return (
    <div
      dir={direction}
      className="relative py-10 max-container"
      id="important-services"
      ref={ref}
    >
      <div className="text-center">
        <CustomTitle title={t("importantServices.title")} />
        <p className="mt-4 text-gray-700">
          {t("importantServices.description")}
        </p>
      </div>

      <div className="relative mt-5 md:mt-10">
        {loading ? (
          <p className="text-center text-gray-600">{t("Loading...")}</p>
        ) : error ? (
          <p className="text-center text-red-500">
            {t("Error")}: {error}
          </p>
        ) : allStores?.length === 0 ? (
          <p className="text-center text-gray-500">{t("No data found")}</p>
        ) : (
          <div className="px-4">
            <Slider key={direction} ref={sliderRef} {...sliderSettings}>
              {allStores?.map((store) => (
                <div key={store._id} className="px-2">
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      handleStoreClick(slugify(store.title.en), store)
                    }
                  >
                    <CustomCardImportantServices
                      image={store.img}
                      title={store.title[i18n.language] || store.title.en}
                    />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>

      {/* Custom CSS for better arrow positioning */}
      <style jsx>{`
        .slick-slider {
          position: relative;
        }

        .slick-slide {
          outline: none;
        }

        .slick-slide > div {
          height: 100%;
        }

        @media (max-width: 768px) {
          .slick-prev,
          .slick-next {
            display: none !important;
          }
        }
        // [dir="ltr"] .slick-track {
        //   direction: rtl;
        // }
      `}</style>
    </div>
  );
});

export default ImportantServicesSlider;
